<template>
  <b-modal
    id="modal-batch-approval-list"
    ref="modalBatchApprovalList"
    title="Select Batch for Approval"
    size="xl"
    hide-footer
    @shown="fetchBatches"
    @hidden="resetModal">
    <div>
      <!-- Course Info Header -->
      <div class="mb-4">
        <h5 class="mb-2">{{ selectedCourse?.title }}</h5>
        <p class="text-muted mb-0">{{ selectedCourse?.description }}</p>
      </div>

      <div class="row mb-3">
        <div class="col-md-4">
          <div class="form-group">
            <label class="form-label">Status</label>
            <v-select
              v-model="selectedStatus"
              :options="participantStatusOptions"
              :reduce="option => option.value"
              label="label"
              placeholder="Semua status"
              :clearable="true">
            </v-select>
          </div>
        </div>
        <div class="col-md-4">
          <div class="form-group">
            <label class="form-label">Approval</label>
            <v-select
              v-model="selectedApprovalStep"
              :options="approvalStepOptions"
              :reduce="option => option.value"
              label="label"
              placeholder="Semua approval"
              :clearable="true">
            </v-select>
          </div>
        </div>
        <div class="col-md-4">
          <div class="form-group">
            <label class="form-label">Course Type</label>
            <v-select
              v-model="selectedCourseType"
              :options="courseTypeOptions"
              :reduce="option => option.value"
              label="label"
              placeholder="Semua tipe"
              :clearable="true">
            </v-select>
          </div>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="batchLoading" class="text-center py-5">
        <div class="spinner-border text-primary" role="status">
          <span class="sr-only">Loading...</span>
        </div>
        <p class="mt-2 text-muted">Loading batches...</p>
      </div>

      <!-- Empty State -->
      <div v-else-if="batches.length === 0" class="text-center py-5">
        <i class="fe fe-inbox" style="font-size: 3rem; color: #dee2e6;"></i>
        <p class="mt-3 text-muted mb-0">No batches available</p>
      </div>

      <!-- Batch Table -->
      <div v-else>
        <vue-good-table
          :columns="batchColumns"
          :rows="batches"
          :pagination-options="{ enabled: true }"
        >
          <template slot="table-row" slot-scope="props">
            <span v-if="props.column.field === 'batch'">
              {{ formatBatchLabel(props.row) }}
            </span>
            <span v-else-if="props.column.field === 'penyelenggara'">
              {{ formatPenyelenggara(props.row.penyelenggara) }}
            </span>
            <span v-else-if="props.column.field === 'tanggal'">
              <template v-for="schedules in [getBatchSchedules(props.row)]">
                <div v-if="schedules.length">
                  <div
                    v-for="(schedule, idx) in schedules"
                    :key="idx"
                    :class="{ 'mt-2': idx > 0 }"
                  >
                    <strong class="d-block">{{ schedule.date }}</strong>
                    <small v-if="schedule.time" class="text-muted">{{ schedule.time }}</small>
                  </div>
                </div>
                <span v-else class="text-muted">No schedule</span>
              </template>
            </span>
            <span v-else-if="props.column.field === 'batch_status'">
              <span class="badge" :class="getBatchStatusClass(props.row.batch_status)">
                {{ props.row.batch_status }}
              </span>
            </span>
            <span v-else-if="props.column.field === 'action'">
              <button class="btn btn-sm btn-outline-primary" @click="selectBatch(props.row, props.index)">
                <i class="fe fe-users me-1"></i> View
              </button>
            </span>
            <span v-else>
              {{ props.formattedRow[props.column.field] }}
            </span>
          </template>
        </vue-good-table>
      </div>
    </div>
  </b-modal>
</template>

<script>
import { mapGetters } from 'vuex';
import { VueGoodTable } from 'vue-good-table';
import 'vue-good-table/dist/vue-good-table.css';
import { normalizeErrorMessage } from '@/utils/error-message';
import { formatBatchLabel, formatPenyelenggara, getBatchSchedules } from '@/utils/batchIdentity';
import {
  APPROVAL_STEP_SLUG_OPTIONS,
  COURSE_TYPE_BOOLEAN_OPTIONS,
  PARTICIPANT_STATUS_OPTIONS,
  buildMitraFilterParams
} from '@/constants/mitraFilters';

export default {
  name: 'ModalBatchApprovalList',
  props: {
    selectedCourse: {
      type: Object,
      default: null
    },
    targetTrainingEventId: {
      type: [String, Number],
      default: null
    },
    idpPeriod: {
      type: [Number, String],
      default: null
    },
    company: {
      type: String,
      default: null
    }
  },
  components: {
    VueGoodTable
  },
  computed: {
    ...mapGetters('mitraEnrollmentReport', ['batches', 'batchLoading'])
  },
  data() {
    return {
      selectedStatus: null,
      selectedApprovalStep: null,
      selectedCourseType: null,
      participantStatusOptions: PARTICIPANT_STATUS_OPTIONS,
      approvalStepOptions: APPROVAL_STEP_SLUG_OPTIONS,
      courseTypeOptions: COURSE_TYPE_BOOLEAN_OPTIONS,
      batchColumns: [
        { label: 'Batch', field: 'batch' },
        { label: 'Penyelenggara', field: 'penyelenggara', width: '180px' },
        { label: 'Tanggal Pelaksanaan', field: 'tanggal', width: '240px' },
        { label: 'Status', field: 'batch_status' },
        { label: 'Quota', field: 'quota' },
        { label: 'Enrolled', field: 'enrolled' },
        { label: 'Available', field: 'available_quota' },
        { label: 'Participants', field: 'action', width: '150px' }
      ]
    };
  },
  watch: {
    selectedStatus() {
      this.refetchBatchesIfVisible();
    },
    selectedApprovalStep() {
      this.refetchBatchesIfVisible();
    },
    selectedCourseType() {
      this.refetchBatchesIfVisible();
    }
  },
  methods: {
    participantFilterParams() {
      return buildMitraFilterParams({
        status: this.selectedStatus,
        approval_step: this.selectedApprovalStep,
        course_type: this.selectedCourseType
      }, { includeParticipantStatusFromApproval: true });
    },
    refetchBatchesIfVisible() {
      if (!this.$refs.modalBatchApprovalList || !this.$refs.modalBatchApprovalList.isVisible) return;
      this.fetchBatches();
    },
    async fetchBatches() {
      if (!this.selectedCourse?.id) return;
      
      try {
        const params = {
          ...this.participantFilterParams()
        };
        if (this.idpPeriod) {
          params.idp_period = this.idpPeriod;
        }
        if (this.company) {
          params.company = this.company;
        }

        await this.$store.dispatch('mitraEnrollmentReport/getBatchesApproval', {
          courseId: this.selectedCourse.id,
          params
        });
        this.openTargetBatch();
      } catch (error) {
        console.error('Error fetching batches:', error);
        this.$swal({
          title: 'Error',
          text: normalizeErrorMessage(error.message || 'Daftar batch belum berhasil dimuat'),
          icon: 'error',
          confirmButtonText: 'OK',
          confirmButtonColor: '#e63757'
        });
      }
    },
    
    resetModal() {
      this.selectedStatus = null;
      this.selectedApprovalStep = null;
      this.selectedCourseType = null;
    },
    
    selectBatch(batch, index) {
      this.$emit('batch-selected', { batch, index });
    },

    openTargetBatch() {
      if (!this.targetTrainingEventId) return;

      const index = (this.batches || []).findIndex(batch => String(batch.training_event_id) === String(this.targetTrainingEventId));
      if (index < 0) return;

      this.selectBatch(this.batches[index], index);
    },
    
    formatBatchLabel,
    formatPenyelenggara,
    getBatchSchedules,
    getBatchStatusClass(status) {
      const classes = {
        'upcoming': 'badge-soft-warning',
        'ongoing': 'badge-soft-success',
        'completed': 'badge-soft-info',
        'cancelled': 'badge-soft-danger'
      };
      return classes[status?.toLowerCase()] || 'badge-soft-secondary';
    }
  }
};
</script>

<style scoped>
.me-1 {
  margin-right: 0.25rem;
}

.badge-soft-warning {
  color: #f6c343;
  background-color: rgba(246, 195, 67, 0.1);
}

.badge-soft-success {
  color: #00c9a7;
  background-color: rgba(0, 201, 167, 0.1);
}

.badge-soft-info {
  color: #39afd1;
  background-color: rgba(57, 175, 209, 0.1);
}

.badge-soft-danger {
  color: #e63757;
  background-color: rgba(230, 55, 87, 0.1);
}

.badge-soft-secondary {
  color: #6c757d;
  background-color: rgba(108, 117, 125, 0.1);
}
</style>
